<template>
    <template-layout>
        <template slot="header">
            <h1 class="title">Checkout</h1>
        </template>

        <template slot="content">
            <b-loading :is-full-page="true" :active.sync="isLoading"></b-loading>

            <section>
                <div v-if="completed === false" class="container">
                    <div class="columns">
                        <div class="column is-three-quarters">
                            <!-- Checkout Step 1 -->
                            <template v-if="step === 1">
                                <div class="checkout-step">
                                    <div class="header is-marginless">
                                        <h1 class="title">Dirección de Entrega</h1>
                                    </div>

                                    <checkout-delivery-address-select
                                        v-model="deliveryAddress"
                                    />
                                </div>

                                <!-- Delivery schedule -->
                                <div class="checkout-step">
                                    <div class="header is-marginless">
                                        <h1 class="title">Fecha y Horario</h1>
                                    </div>

                                    <checkout-delivery-schedule
                                        v-model="deliverySchedule"
                                        :zone="this.deliveryZone"
                                        :address="this.deliveryAddress"
                                    />
                                </div>
                            </template>

                            <!-- Checkout Step 2 -->
                            <template v-if="step === 2">
                                <!-- Payment information -->
                                <div class="checkout-step">
                                    <div class="header is-marginless">
                                        <h1 class="title">Forma de pago</h1>
                                    </div>

                                    <checkout-payment-select
                                        ref="paymentForm"
                                        v-model="paymentMethod"
                                    />
                                </div>
                            </template>

                            <!-- Checkout Step 3 -->
                            <template v-if="step === 3">
                                <!--Billing Address-->
                                <div class="checkout-step">
                                    <div class="header is-marginless">
                                        <h1 class="title">Datos para tu Factura</h1>
                                    </div>

                                    <checkout-billing-address-select
                                        v-model="billingAddress"
                                    />
                                </div>
                            </template>

                            <!-- Checkout Step 4 -->
                            <template v-if="step === 4">
                                <div class="checkout-step">
                                    <div class="header is-marginless">
                                        <h1 class="title">Revisá los datos</h1>
                                    </div>

                                    <h1 class="subtitle">Entrega</h1>
                                    <t-info-list-item>
                                        <template v-slot:icon>
                                            <i class="fas fa-2x fa-map-marker-alt"></i>
                                        </template>

                                        <template v-slot:title>
                                            <template v-if="deliveryAddress.place">{{ deliveryAddress.place }}</template>
                                            {{ deliveryAddress.address }}
                                        </template>

                                        <template v-slot:subtitle>
                                            Piso y/o Unidad: {{ deliveryAddress.unit }} - Tel: {{ deliveryAddress.phone }} - Cel: {{ deliveryAddress.cellphone }}
                                        </template>

                                        <template v-slot:actions>
                                            <a @click="onChangeDeliverySelected">Cambiar</a>
                                        </template>
                                    </t-info-list-item>

                                    <t-info-list-item>
                                        <template v-slot:icon>
                                            <i class="fas fa-2x fa-clock"></i>
                                        </template>

                                        <template v-slot:title>
                                            {{ deliverySchedule.date | moment('dddd D') | capitalize }} de {{ deliverySchedule.date | moment('MMMM') | capitalize }}
                                        </template>

                                        <template v-slot:subtitle>
                                            {{ deliverySchedule.start }} a {{ deliverySchedule.end }}
                                        </template>

                                        <template v-slot:actions>
                                            <a @click="onChangeDeliverySelected">Cambiar</a>
                                        </template>
                                    </t-info-list-item>

                                    <h1 class="subtitle">Pago</h1>
                                    <t-info-list-item>
                                        <template v-slot:icon>
                                            <span class="icon is-large"><i v-bind:class="[ getIcon(paymentMethod) ]"></i></span>
                                        </template>

                                        <template v-slot:title>
                                            <template v-if="paymentMethod.method === 5">
                                                Tu {{ paymentMethod.cc_type }} terminada en {{ paymentMethod.cc_last }}
                                            </template>
                                            <template v-else-if="paymentMethod.method === -1"> 
                                                Pago en la entrega (Crédito, Débito, Efectivo)
                                            </template>
                                            <template v-else> 
                                                {{ paymentMethod.name }}
                                            </template>
                                        </template>

                                        <template v-slot:actions>
                                            <a @click="onChangePaymentMethodSelected">Cambiar</a>
                                        </template>
                                    </t-info-list-item>

                                    <h1 class="subtitle">Datos para tu factura</h1>
                                    
                                    <checkout-billing-address-select
                                        v-model="billingAddress"
                                    />
                                </div>

                                <div class="checkout-step">
                                    <div class="header is-marginless">
                                        <h1 class="title">Comentario</h1>
                                    </div>

                                    <checkout-comment v-model="comment"></checkout-comment>
                                </div>
                            </template>
                        </div>

                        <!--Order Details-->
                        <div class="column">
                            <checkout-order-detail
                                class="is-hidden-mobile"
                                :cart="cart"
                            >
                            </checkout-order-detail>

                            <p v-for="error in cart.errors" :key="error" style="margin-bottom: 40px;" class="has-text-danger">
                                <!-- <p ></p> -->
                                {{ $t(error) }}
                            </p>

                            <button class="button is-primary is-large" v-on:click="onPlaceOrder" :disabled="!canSubmit || step !== 4">
                                Confirmar
                            </button>
                        </div>
                    </div>
                </div>
                <div v-else class="container has-text-centered">
                    <checkout-complete></checkout-complete>
                </div>
            </section>
        </template>
    </template-layout>
</template>

<script>
    import TemplateLayout from '../../layout/TemplateLayout.vue';

    import CheckoutDeliveryAddressSelect from './CheckoutDeliveryAddressSelect';
    import CheckoutDeliverySchedule from './CheckoutDeliverySchedule';
    import CheckoutPaymentSelect from './CheckoutPaymentSelect';
    import CheckoutBillingAddressSelect from './CheckoutBillingAddressSelect';

    import CheckoutComplete from './CheckoutComplete';
    import CheckoutOrderDetail from './CheckoutOrderDetail';
    import CheckoutComment from './CheckoutComment';

    import TInfoListItem from '../../components/ui/TInfoListItem';

    export default {
        name: 'checkout',

        data() {
            return {
                isLoading: false,
                step: 1,
                completed: false,

                deliveryAddress: null,
                deliverySchedule: null,
                paymentMethod: null,
                billingAddress: null,

                deliveryZone: null,

                discounts: [],

                comment: null,

                invalidPaymentMethod: false
            }
        },

        components: {
            TemplateLayout,
            CheckoutDeliveryAddressSelect,
            CheckoutDeliverySchedule,
            CheckoutPaymentSelect,
            CheckoutBillingAddressSelect,
            CheckoutComplete,
            CheckoutOrderDetail,
            CheckoutComment,
            TInfoListItem
        },

        beforeRouteEnter(to, from, next) {
            next(vm => {
                let cartItems =  vm.$store.getters['cart/get']().items;
                if(cartItems.length <= 0) {
                    next({
                        path: '/products'
                    });
                } else if(vm.$store.state.user.verified === false) {
                    next({
                        path: '/validate'
                    });
                }
            })
        },

        computed: {
            customer() {
                return this.$store.getters['customer/get']();
            },

            cart() {
                return this.$store.getters['cart/get']();
            },

            cartItems() {
                return this.$store.getters['cart/get']().items;
            },

            cartTotal() {
                return this.$store.getters['cart/total'];
            },

            shippingCost() {
                return this.deliveryZone ? this.deliveryZone.shippingCost : 0;
            },

            orderTotal() {
                let total = this.cartTotal + this.shippingCost;

                if(this.discounts) {
                    for(let i = 0; i < this.discounts.length; ++i) {
                        total -= this.discounts[i].amount;
                    }
                }

                return total;
            },

            canSubmit() {
                let canSubmit = this.deliveryAddress != null &&
                    this.deliveryZone != null &&
                    this.deliverySchedule != null &&
                    this.billingAddress != null &&
                    this.invalidPaymentMethod === false &&
                    this.cart.errors.length === 0;

                return canSubmit;
            }
        },

        watch: {
            deliveryAddress(value) {
                if(!value)
                    return;

                this.$store.dispatch('cart/setShippingAddress', {
                    customer: this.customer,
                    address: value
                });

                // Fetch the delivery zone for this address
                this.deliveryZone = null;
                this.$api.getZonesByLocation(value.location).then((zone) => {
                    this.deliveryZone = zone;
                });
            },

            deliverySchedule(value) {
                this.$store.dispatch('cart/setDeliverySchedule', {
                    schedule: value
                });

                // Once we selected a schedule we can move to step 2
                this.step++;
            },

            paymentMethod(value) {
                this.invalidPaymentMethod = false;

                this.step++;

                if(this.billingAddress)
                    this.step++;
            },

            billingAddress(value) {
                this.$store.dispatch('cart/setBillingAddress', {
                    address: value
                });

                if(this.step === 3)
                    this.step++;
            }
        },

        methods: {
            getIcon(method) {
                switch(method.method) {
                    case 1:
                        return 'fas fa-2x fa-money-bill-wave';
                    case 2:
                        return 'fas fa-2x fa-money-check-alt';
                    case 3:
                        return 'fas fa-2x fa-address-card';
                    case 4:
                        return 'fas fa-2x fa-fax';
                    case 5:
                        return 'fas fa-2x fa-credit-card';
                }
            },

            onChangePaymentMethodSelected() {
                this.step = 2;
            },

            onChangeDeliverySelected() {
                this.deliveryAddress = null;
                this.step = 1;
            },

            async onPlaceOrder() {
                this.isLoading = true;

                try {
                    await this.$store.dispatch('orders/create', {
                        pendingOrder: this.cart.id,
                        payment_method_id: this.paymentMethod.method,
                        payment_info: this.paymentMethod,
                        comment: this.comment
                    });
                    
                    this.completed = true;

                    this.$store.dispatch('cart/clear');

                    this.scrollToTop();
                }
                catch(error) {
                    this.isLoading = false;

                    if(error.code === 'error.payment.invalid') {
                        this.invalidPaymentMethod = true;

                        this.$buefy.dialog.alert({
                            title: 'Error',
                            message: 'Tu método de pago fue rechazado, por favor revisá los datos o intentá con otro medio de pago',
                            type: 'is-danger',
                            hasIcon: true,
                            icon: 'times-circle',
                            iconPack: 'fa'
                        });
                    }
                    else if(error.code === 'error.order.invalid_schedule') {
                        this.$buefy.dialog.alert({
                            title: 'Error',
                            message: 'El horario de entrega ya no esta disponible. Intente con otro horario.',
                            type: 'is-danger',
                            hasIcon: true,
                            icon: 'times-circle',
                            iconPack: 'fa'
                        });
                    }
                    else {
                        this.$buefy.dialog.alert({
                            title: 'Error',
                            message: 'Ha ocurrido un error al realizar el pedido. Vuelve a intentarlo.',
                            type: 'is-danger',
                            hasIcon: true,
                            icon: 'times-circle',
                            iconPack: 'fa'
                        });
                    }
                }
                finally {
                    this.isLoading = false;
                }
            },

            scrollToTop() {
                window.scrollTo(0, 0);
            }
        },
    }
</script>

<style lang="scss" scoped>
    @import "./../../assets/css/checkout.scss";

    .subtitle {
        margin-top: 30px;
    }
</style>